import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { ChatMessage, ChartSpec, ToolStatus, streamChat } from 'clients/ChatClient';

interface ChatContextValue {
    messages: ChatMessage[];
    isStreaming: boolean;
    activeTools: string[];
    error: string | null;
    sendMessage: (content: string) => void;
    clearChat: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

interface ChatProviderProps {
    spotUuid: string;
    children: React.ReactNode;
}

export function ChatProvider({ spotUuid, children }: ChatProviderProps): JSX.Element {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [activeTools, setActiveTools] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const sendMessage = useCallback(
        (content: string) => {
            if (isStreaming || !content.trim()) return;

            const userMessage: ChatMessage = { role: 'user', content: content.trim() };
            const updatedMessages = [...messages, userMessage];
            setMessages(updatedMessages);
            setIsStreaming(true);
            setError(null);
            setActiveTools([]);

            const assistantMessage: ChatMessage = { role: 'assistant', content: '', charts: [] };
            setMessages([...updatedMessages, assistantMessage]);

            abortRef.current = new AbortController();

            streamChat(
                spotUuid,
                updatedMessages,
                (token) => {
                    setActiveTools([]);
                    assistantMessage.content += token;
                    setMessages((prev) => {
                        const next = [...prev];
                        next[next.length - 1] = { ...assistantMessage };
                        return next;
                    });
                },
                () => {
                    setIsStreaming(false);
                    setActiveTools([]);
                },
                (errorMsg) => {
                    setError(errorMsg);
                    setIsStreaming(false);
                    setActiveTools([]);
                },
                (toolStatus: ToolStatus) => {
                    if (toolStatus.status === 'calling') {
                        setActiveTools((prev) => [...prev, toolStatus.tool]);
                    }
                },
                (chart: ChartSpec) => {
                    if (!assistantMessage.charts) assistantMessage.charts = [];
                    assistantMessage.charts.push(chart);
                    setMessages((prev) => {
                        const next = [...prev];
                        next[next.length - 1] = { ...assistantMessage };
                        return next;
                    });
                },
                abortRef.current.signal
            );
        },
        [spotUuid, messages, isStreaming]
    );

    const clearChat = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
        setMessages([]);
        setIsStreaming(false);
        setActiveTools([]);
        setError(null);
    }, []);

    return (
        <ChatContext.Provider value={{ messages, isStreaming, activeTools, error, sendMessage, clearChat }}>
            {children}
        </ChatContext.Provider>
    );
}

export function useChatContext(): ChatContextValue {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext must be used within a ChatProvider');
    }
    return context;
}
